// 🎓 Certifications — employee safety certificates (Phase 1, staging).
//
// Three sub-views:
//   • Find crew  — Patrick's screen: "who holds ALL of certs X valid on date Y",
//                  with Download / Email-to-client off the checked rows.
//   • Matrix     — employees × cert types, colour-coded, printable.
//   • Manage     — Paula's screen (owner / road_super / admin): the NEEDS REVIEW
//                  queue (what the OneDrive importer — and, from Phase 2, a
//                  foreman's phone — left for her to classify and date), the
//                  expiring & expired worklist, per-employee Add / Replace /
//                  Edit / Remove, and the STANDARD cert list editor.
//
// Every decision about who may do what is the SERVER's (api/certs/index.js):
// this file renders what `can_manage` says and never infers it from the role.
// A foreman never reaches this tab (Dashboard hides it and the API 403s him).
//
// ⚠ CERT TYPE IS A <select> OVER cert_types, NEVER A TEXT BOX. The whole point
// of the module is that "Osha 30", "OSHA-30" and "Osha 30 Certification" stop
// being three different things. A missing type is added to the standard list
// first (Manage → Standard cert list), then picked. Same reasoning as the
// no-typed-in-cost-codes rule on My Time.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { certsGet, certsPost, uploadCertFile } from '../lib/db'

// ── small helpers (display only; the server owns the rules) ──────────────────
function fmtDate(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}
function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
}
// Mirrors addMonths() in api/lib/certs.js — used ONLY to pre-fill the expiry
// field from default_valid_months. The server stores exactly what it is sent.
function addMonthsYmd(ymd, months) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1 + Number(months || 0), d))
  if (t.getUTCDate() !== d) t.setUTCDate(0)
  return t.toISOString().slice(0, 10)
}
const mb = (bytes) => `${((Number(bytes) || 0) / 1048576).toFixed(1)} MB`

const STATE_STYLE = {
  valid:          { bg: '#dcfce7', fg: '#166534', label: 'Valid' },
  expiring:       { bg: '#fef3c7', fg: '#92400e', label: 'Expiring' },
  expired:        { bg: '#fee2e2', fg: '#991b1b', label: 'Expired' },
  no_expiry:      { bg: '#dcfce7', fg: '#166534', label: 'No expiry' },
  pending_review: { bg: '#dbeafe', fg: '#1e40af', label: 'Pending review' },
  superseded:     { bg: '#f3f4f6', fg: '#6b7280', label: 'Superseded' },
  rejected:       { bg: '#f3f4f6', fg: '#6b7280', label: 'Removed' },
  none:           { bg: '#f3f4f6', fg: '#9ca3af', label: '—' },
}
function Pill({ state, text }) {
  const s = STATE_STYLE[state] || STATE_STYLE.none
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9, fontSize: 11, fontWeight: 600, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{text || s.label}</span>
}
function Msg({ msg }) {
  if (!msg) return null
  const kind = msg.kind || 'error'
  const st = kind === 'ok' ? { bg: '#ecfdf5', bd: '#10b981', fg: '#065f46' } : kind === 'warn' ? { bg: '#fffbeb', bd: '#f59e0b', fg: '#92400e' } : { bg: '#fdecec', bd: '#d9534f', fg: '#a12622' }
  return <div style={{ background: st.bg, borderLeft: `4px solid ${st.bd}`, color: st.fg, padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 13, margin: '8px 0' }}>{msg.text}</div>
}
const errText = (r, fallback) => (r && (r.detail || r.error)) ? `${fallback}: ${r.detail || r.error}` : fallback

const inp = { padding: '6px 8px', fontSize: 13, border: '1px solid #cfd6dd', borderRadius: 4, fontFamily: 'inherit', background: '#fff' }
const th = { padding: '6px 8px', fontSize: 11, fontWeight: 700, color: '#4a5568', textAlign: 'left', borderBottom: '2px solid #cfd6dd', whiteSpace: 'nowrap', background: '#f7f9fb', position: 'sticky', top: 0, zIndex: 1 }
const td = { padding: '5px 8px', fontSize: 13, borderBottom: '1px solid #eef0f2', verticalAlign: 'middle' }
const card = { background: '#fff', border: '0.5px solid #d9dee3', borderRadius: 10, padding: 14, marginBottom: 12 }
const subTab = (on) => ({ padding: '6px 12px', fontSize: 13, cursor: 'pointer', borderRadius: 6, border: '1px solid ' + (on ? '#1e2d3d' : '#cfd6dd'), background: on ? '#1e2d3d' : '#fff', color: on ? '#fff' : '#1e2d3d', fontWeight: on ? 600 : 500 })

// Download ONE cert file through a 5-minute signed URL. The signed URL carries a
// Content-Disposition: attachment, so an <a> click saves it without leaving the page.
async function downloadCert(certId) {
  const r = await certsGet('file', { cert_id: certId })
  if (r.error) return r
  const a = document.createElement('a')
  a.href = r.url
  a.download = r.file_name || ''
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  return r
}
async function downloadMany(certIds, onProgress) {
  let n = 0
  for (const id of certIds) {
    const r = await downloadCert(id)
    if (!r.error) n++
    if (onProgress) onProgress(n, certIds.length)
    await new Promise(res => setTimeout(res, 450))
  }
  return n
}

// ═════════════════════════════════════════════════════════════════════════════
export default function CertsView({ session, role, userEmail }) {
  const [opts, setOpts] = useState(null)
  const [optsErr, setOptsErr] = useState('')
  const [sub, setSub] = useState('find')
  const [openEmp, setOpenEmp] = useState(null) // employee id whose cert panel is open
  const [refreshKey, setRefreshKey] = useState(0)

  const loadOptions = useCallback(async () => {
    const r = await certsGet('options')
    if (r.error) { setOptsErr(errText(r, 'Could not load the certifications module')); return }
    setOpts(r)
  }, [])
  useEffect(() => { loadOptions() }, [loadOptions, refreshKey])

  const canManage = !!(opts && opts.can_manage)
  const bump = () => setRefreshKey(k => k + 1)

  if (optsErr) return <div style={{ padding: 16 }}><Msg msg={{ kind: 'error', text: optsErr }} /></div>
  if (!opts) return <div style={{ padding: 24, color: '#6b7280', fontSize: 14 }}>Loading certifications…</div>

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2d3d', marginRight: 8 }}>🎓 Certifications</div>
        <button style={subTab(sub === 'find')} onClick={() => setSub('find')}>Find crew</button>
        <button style={subTab(sub === 'matrix')} onClick={() => setSub('matrix')}>Matrix</button>
        {canManage && <button style={subTab(sub === 'manage')} onClick={() => setSub('manage')}>Manage</button>}
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto' }}>
          {opts.cert_types.filter(t => t.active).length} cert types · {opts.employees.filter(e => e.active !== false).length} active employees
        </span>
      </div>

      {sub === 'find' && <FindCrew opts={opts} onOpenEmployee={setOpenEmp} refreshKey={refreshKey} />}
      {sub === 'matrix' && <Matrix opts={opts} onOpenEmployee={setOpenEmp} refreshKey={refreshKey} />}
      {sub === 'manage' && canManage && <Manage opts={opts} onOpenEmployee={setOpenEmp} onTypesChanged={bump} refreshKey={refreshKey} />}

      {openEmp != null && (
        <EmployeeCertsModal
          employeeId={openEmp} opts={opts} canManage={canManage}
          onClose={() => setOpenEmp(null)} onChanged={bump}
        />
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// FIND CREW
function FindCrew({ opts, onOpenEmployee, refreshKey }) {
  const types = useMemo(() => opts.cert_types.filter(t => t.active), [opts])
  const [picked, setPicked] = useState(() => new Set())
  const [validOn, setValidOn] = useState(opts.today || todayET())
  const [activeOnly, setActiveOnly] = useState(true)
  const [schedOnly, setSchedOnly] = useState(false)
  const [res, setRes] = useState(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const [checked, setChecked] = useState(() => new Set())
  const [compose, setCompose] = useState(false)

  const toggleType = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

  const run = async () => {
    if (picked.size === 0) { setMsg({ kind: 'warn', text: 'Pick at least one certification.' }); return }
    setBusy(true); setMsg(null); setChecked(new Set())
    const r = await certsGet('search', { cert_type_ids: [...picked].join(','), valid_on: validOn, active_only: activeOnly, schedulable_only: schedOnly })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Search failed') }); return }
    setRes(r)
  }
  // Re-run after an edit elsewhere so the list never goes stale under the user.
  useEffect(() => { if (res && picked.size) run() }, [refreshKey])

  const results = res ? res.results : []
  const allChecked = results.length > 0 && results.every(r => checked.has(r.employee.id))
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(results.map(r => r.employee.id)))
  const toggleRow = (id) => setChecked(c => { const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n })

  const selectedCerts = useMemo(() => results
    .filter(r => checked.has(r.employee.id))
    .flatMap(r => r.certs.filter(c => c && c.has_file).map(c => ({ ...c, employee_name: r.employee.name, cert_type_name: (res.cert_types.find(t => String(t.id) === String(c.cert_type_id)) || {}).name })))
  , [results, checked, res])

  const doDownload = async () => {
    if (selectedCerts.length === 0) { setMsg({ kind: 'warn', text: 'None of the checked rows have a file on record.' }); return }
    setBusy(true)
    const n = await downloadMany(selectedCerts.map(c => c.id), (done, total) => setMsg({ kind: 'ok', text: `Downloading ${done} of ${total}…` }))
    setBusy(false)
    setMsg({ kind: n === selectedCerts.length ? 'ok' : 'warn', text: `${n} of ${selectedCerts.length} file${selectedCerts.length === 1 ? '' : 's'} downloaded.` })
  }

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#4a5568', marginBottom: 6 }}>Must hold ALL of these certifications</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {types.map(t => (
            <label key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 14, border: '1px solid ' + (picked.has(t.id) ? '#1e2d3d' : '#cfd6dd'), background: picked.has(t.id) ? '#eef2f7' : '#fff', fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={picked.has(t.id)} onChange={() => toggleType(t.id)} style={{ margin: 0 }} />
              {t.short_name || t.name}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 6 }}>Valid on
            <input type="date" value={validOn} onChange={e => setValidOn(e.target.value)} style={inp} />
          </label>
          <label style={{ fontSize: 12, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 5 }}>
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active employees only
          </label>
          <label style={{ fontSize: 12, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 5 }}>
            <input type="checkbox" checked={schedOnly} onChange={e => setSchedOnly(e.target.checked)} /> On-board (schedulable) only
          </label>
          <button className="btn btn-primary" onClick={run} disabled={busy || picked.size === 0}>{busy ? 'Searching…' : 'Find crew'}</button>
        </div>
      </div>
      <Msg msg={msg} />

      {res && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2d3d' }}>
              {results.length} {results.length === 1 ? 'person holds' : 'people hold'} {res.cert_types.map(t => t.short_name || t.name).join(' + ')} valid on {fmtDate(res.valid_on)}
            </div>
            <span style={{ fontSize: 11, color: '#6b7280' }}>amber = expires within 60 days</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" disabled={busy || checked.size === 0} onClick={doDownload}>⬇ Download files ({selectedCerts.length})</button>
              <button className="btn btn-sm btn-primary" disabled={busy || checked.size === 0} onClick={() => setCompose(true)}>✉ Email to client ({selectedCerts.length})</button>
            </div>
          </div>
          {results.length === 0 ? (
            <div style={{ fontSize: 13, color: '#6b7280', padding: '10px 0' }}>Nobody holds every one of those certifications on that date. Try fewer certifications, or check the Matrix for who is close.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={th}><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
                  <th style={th}>Emp #</th><th style={th}>Name</th><th style={th}>Trade</th>
                  {res.cert_types.map(t => <th key={t.id} style={th}>{t.short_name || t.name}</th>)}
                  <th style={th}></th>
                </tr></thead>
                <tbody>
                  {results.map(r => (
                    <tr key={r.employee.id} style={{ background: checked.has(r.employee.id) ? '#f5f8fc' : undefined }}>
                      <td style={td}><input type="checkbox" checked={checked.has(r.employee.id)} onChange={() => toggleRow(r.employee.id)} /></td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: '#6b7280' }}>{r.employee.id}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{r.employee.name}{r.employee.active === false && <span style={{ color: '#9ca3af', fontWeight: 400 }}> · inactive</span>}</td>
                      <td style={td}>{r.employee.local || ''}</td>
                      {r.certs.map((c, i) => (
                        <td key={i} style={td}>
                          {c ? <Pill state={c.state === 'expiring' ? 'expiring' : c.state === 'no_expiry' ? 'no_expiry' : 'valid'} text={c.expires_date ? fmtDate(c.expires_date) : 'no expiry'} /> : <Pill state="none" />}
                          {c && !c.has_file && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 4 }}>no file</span>}
                        </td>
                      ))}
                      <td style={td}><button className="btn btn-sm" onClick={() => onOpenEmployee(r.employee.id)}>All certs</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {compose && (
        <EmailPacketModal certs={selectedCerts} onClose={() => setCompose(false)}
          onSent={(t) => { setCompose(false); setMsg({ kind: 'ok', text: t }) }} />
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// EMAIL PACKET
function EmailPacketModal({ certs, onClose, onSent }) {
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState(`Safety certifications — Cornerstone Contracting (${certs.length} file${certs.length === 1 ? '' : 's'})`)
  const [message, setMessage] = useState('Please find the requested safety certifications attached.')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const total = certs.reduce((t, c) => t + (Number(c.file_size) || 0), 0)
  const tooMany = certs.length > 10
  const tooBig = total > 20 * 1048576
  const split = (s) => s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean)

  const send = async () => {
    const toList = split(to)
    if (toList.length === 0) { setMsg({ kind: 'warn', text: 'Enter at least one recipient.' }); return }
    setBusy(true); setMsg(null)
    const r = await certsPost('email-packet', { cert_ids: certs.map(c => c.id), to: toList, cc: split(cc), subject, message })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Send failed') }); return }
    onSent(`Sent ${r.sent} file${r.sent === 1 ? '' : 's'} to ${r.to.join(', ')}.`)
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Email certifications to a client</div>
        <div className="modal-sub">Sent from the Crew Dispatch address with your email as reply-to. Each file is a separate attachment.</div>
        <div className="modal-row"><label>To</label><input style={{ ...inp, flex: 1 }} value={to} onChange={e => setTo(e.target.value)} placeholder="safety@client.com, pm@client.com" /></div>
        <div className="modal-row"><label>Cc</label><input style={{ ...inp, flex: 1 }} value={cc} onChange={e => setCc(e.target.value)} placeholder="optional" /></div>
        <div className="modal-row"><label>Subject</label><input style={{ ...inp, flex: 1 }} value={subject} onChange={e => setSubject(e.target.value)} /></div>
        <div className="modal-row" style={{ alignItems: 'flex-start' }}><label>Message</label><textarea style={{ ...inp, flex: 1, minHeight: 70 }} value={message} onChange={e => setMessage(e.target.value)} /></div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#4a5568', margin: '8px 0 4px' }}>Attachments ({certs.length}) · {mb(total)}</div>
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #eef0f2', borderRadius: 6 }}>
          {certs.length === 0 && <div style={{ padding: 10, fontSize: 12, color: '#6b7280' }}>None of the checked rows have a file on record.</div>}
          {certs.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', fontSize: 12, borderBottom: '1px solid #f3f4f6' }}>
              <span>{c.employee_name} — {c.cert_type_name || 'Certificate'}{c.expires_date ? ` (exp ${fmtDate(c.expires_date)})` : ''}</span>
              <span style={{ color: '#6b7280' }}>{mb(c.file_size)}</span>
            </div>
          ))}
        </div>
        {tooMany && <Msg msg={{ kind: 'error', text: `A packet holds at most 10 files — uncheck ${certs.length - 10} employee${certs.length - 10 === 1 ? '' : 's'}.` }} />}
        {tooBig && <Msg msg={{ kind: 'error', text: `The attachments total ${mb(total)}; the limit is 20 MB. Uncheck some rows or send two emails.` }} />}
        <Msg msg={msg} />
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={busy || certs.length === 0 || tooMany || tooBig}>{busy ? 'Sending…' : `Send ${certs.length} file${certs.length === 1 ? '' : 's'}`}</button>
        </div>
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// MATRIX
function Matrix({ opts, onOpenEmployee, refreshKey }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [schedOnly, setSchedOnly] = useState(false)
  const [q, setQ] = useState('')
  const printRef = useRef(null)

  useEffect(() => {
    let alive = true
    setErr('')
    certsGet('matrix', { active_only: activeOnly, schedulable_only: schedOnly }).then(r => {
      if (!alive) return
      if (r.error) { setErr(errText(r, 'Could not load the matrix')); return }
      setData(r)
    })
    return () => { alive = false }
  }, [activeOnly, schedOnly, refreshKey])

  const rows = useMemo(() => {
    if (!data) return []
    const needle = q.trim().toLowerCase()
    return data.employees.filter(e => !needle || String(e.name || '').toLowerCase().includes(needle) || String(e.id).includes(needle))
  }, [data, q])

  function printMatrix() {
    if (!printRef.current || !data) return
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(`<!DOCTYPE html><html><head><title>Certification matrix — ${fmtDate(data.today)}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#1f2430}
      table{border-collapse:collapse} th,td{font-size:10px;padding:3px 5px;border:1px solid #d9dee3} th{background:#f7f9fb}
      /* Browsers strip background fills when printing unless told not to — the
         colours ARE the matrix, so keep them (same rule as HoursProofingModal). */
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @media print { * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } body{margin:8mm} }</style></head><body>
      <div style="font-size:16px;font-weight:700;margin-bottom:2px">Cornerstone Contracting — Certification Matrix</div>
      <div style="font-size:11px;color:#555;margin-bottom:12px">As of ${fmtDate(data.today)} · ${rows.length} employees · green = valid · amber = expires within 60 days · red = expired · blue = pending review · grey = none on file</div>
      ${printRef.current.innerHTML}
      </body></html>`)
    w.document.close()
    w.focus()
    setTimeout(() => { try { w.print() } catch (e) { /* user can print manually */ } }, 250)
  }

  const cellStyle = (c) => {
    const s = STATE_STYLE[c ? c.state : 'none'] || STATE_STYLE.none
    return { ...td, background: s.bg, color: s.fg, textAlign: 'center', cursor: 'pointer', fontSize: 11, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
  }

  return (
    <div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <input style={{ ...inp, width: 220 }} placeholder="Search name or employee #" value={q} onChange={e => setQ(e.target.value)} />
        <label style={{ fontSize: 12, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active only
        </label>
        <label style={{ fontSize: 12, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={schedOnly} onChange={e => setSchedOnly(e.target.checked)} /> On-board only
        </label>
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          <Pill state="valid" /> <Pill state="expiring" text="Expiring ≤60d" /> <Pill state="expired" /> <Pill state="pending_review" /> <Pill state="none" text="None" />
        </span>
        <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={printMatrix} disabled={!data}>🖨 Print</button>
      </div>
      {err && <Msg msg={{ kind: 'error', text: err }} />}
      {!data && !err && <div style={{ color: '#6b7280', fontSize: 13, padding: 12 }}>Loading matrix…</div>}
      {data && (
        <div style={{ ...card, padding: 0, overflow: 'auto', maxHeight: '72vh' }} ref={printRef}>
          <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            <thead><tr>
              <th style={{ ...th, left: 0, zIndex: 2 }}>Emp #</th>
              <th style={{ ...th, left: 0, zIndex: 2 }}>Name</th>
              {data.cert_types.map(t => <th key={t.id} style={{ ...th, textAlign: 'center' }} title={t.name}>{t.short_name || t.name}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(e => (
                <tr key={e.id}>
                  <td style={{ ...td, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>{e.id}</td>
                  <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => onOpenEmployee(e.id)}>{e.name}{e.active === false ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> · inactive</span> : ''}</td>
                  {data.cert_types.map(t => {
                    const c = e.cells[t.id]
                    return (
                      <td key={t.id} style={cellStyle(c)} onClick={() => onOpenEmployee(e.id)} title={c ? `${STATE_STYLE[c.state]?.label || c.state}${c.expires_date ? ` · expires ${fmtDate(c.expires_date)}` : ''}` : 'none on file'}>
                        {c ? (c.state === 'pending_review' ? 'review' : c.expires_date ? fmtDate(c.expires_date) : '✓ no exp') : ''}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {rows.length === 0 && <tr><td style={td} colSpan={2 + data.cert_types.length}>No employees match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// MANAGE (Paula)
function Manage({ opts, onOpenEmployee, onTypesChanged, refreshKey }) {
  // The queue is fetched here (not in its panel) so the badge count is live on
  // every panel and so the DEFAULT panel can be decided on first load: land on
  // "Needs review" whenever anything is waiting, otherwise on the worklist.
  const [panel, setPanel] = useState(null)
  const [pending, setPending] = useState(null)
  const [pendingErr, setPendingErr] = useState('')
  const [empPick, setEmpPick] = useState('')
  const emps = opts.employees
  useEffect(() => {
    let alive = true
    certsGet('pending').then(r => {
      if (!alive) return
      if (r.error) { setPendingErr(errText(r, 'Could not load the review queue')); setPending([]); return }
      setPending(r.items)
    })
    return () => { alive = false }
  }, [refreshKey])
  useEffect(() => {
    if (panel === null && pending !== null) setPanel(pending.length > 0 ? 'review' : 'expiring')
  }, [pending, panel])
  const pendingCount = pending ? pending.length : 0
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={subTab(panel === 'review')} onClick={() => setPanel('review')}>
          Needs review{pendingCount > 0 && <span style={{ marginLeft: 6, background: panel === 'review' ? '#fff' : '#1e40af', color: panel === 'review' ? '#1e2d3d' : '#fff', borderRadius: 9, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{pendingCount}</span>}
        </button>
        <button style={subTab(panel === 'expiring')} onClick={() => setPanel('expiring')}>Expiring &amp; expired</button>
        <button style={subTab(panel === 'types')} onClick={() => setPanel('types')}>Standard cert list</button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 6 }}>
          Open an employee:
          <select style={{ ...inp, minWidth: 220 }} value={empPick} onChange={e => { setEmpPick(e.target.value); if (e.target.value) { onOpenEmployee(Number(e.target.value)); setEmpPick('') } }}>
            <option value="">— pick —</option>
            {emps.filter(e => e.active !== false).map(e => <option key={e.id} value={e.id}>{e.name} (#{e.id})</option>)}
            <optgroup label="Inactive">
              {emps.filter(e => e.active === false).map(e => <option key={e.id} value={e.id}>{e.name} (#{e.id})</option>)}
            </optgroup>
          </select>
        </span>
      </div>
      {pendingErr && <Msg msg={{ kind: 'error', text: pendingErr }} />}
      {panel === 'review' && <NeedsReview items={pending || []} opts={opts} onOpenEmployee={onOpenEmployee} onChanged={onTypesChanged} />}
      {panel === 'expiring' && <ExpiringWorklist onOpenEmployee={onOpenEmployee} refreshKey={refreshKey} />}
      {panel === 'types' && <CertTypesEditor opts={opts} onChanged={onTypesChanged} />}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// NEEDS REVIEW — the queue Paula works. Each row: employee, the original file,
// View (the 5-minute signed URL, opened INLINE so the card can be read), and
// inline fields → Save & activate. The importer pre-selects the type when it
// matched one, so most rows only need dates. Cert type is a <select> over the
// standard list, never free text. The server (classify) refuses to activate an
// expiring type with no expiry date — that is the rule, this is just the form.
function NeedsReview({ items, opts, onOpenEmployee, onChanged }) {
  const activeTypes = opts.cert_types.filter(t => t.active)
  const [drafts, setDrafts] = useState({})
  const [busyId, setBusyId] = useState(null)
  const [msg, setMsg] = useState(null)

  const draftFor = (it) => drafts[it.id] || {
    cert_type_id: it.cert_type_id ? String(it.cert_type_id) : '',
    issued_date: it.issued_date || '', expires_date: it.expires_date || '',
    cert_number: it.cert_number || '', issuing_org: it.issuing_org || '',
    notes: it.notes || '', expires_touched: false,
  }
  const setDraft = (it, patch) => setDrafts(d => ({ ...d, [it.id]: { ...draftFor(it), ...patch } }))
  const typeOf = (d) => activeTypes.find(t => String(t.id) === String(d.cert_type_id)) || null

  // PRE-FILL ONLY (same rule as the add form): issued + default_valid_months
  // → expires until the field is touched. The server stores what it is sent.
  const onIssued = (it, v) => {
    const d = draftFor(it)
    const t = typeOf(d)
    const next = { issued_date: v }
    if (!d.expires_touched && t && t.default_valid_months && v) next.expires_date = addMonthsYmd(v, t.default_valid_months)
    setDraft(it, next)
  }
  const onType = (it, v) => {
    const d = draftFor(it)
    const t = activeTypes.find(x => String(x.id) === v) || null
    const next = { cert_type_id: v }
    if (!d.expires_touched && t && t.default_valid_months && d.issued_date) next.expires_date = addMonthsYmd(d.issued_date, t.default_valid_months)
    setDraft(it, next)
  }

  const view = async (it) => {
    const r = await certsGet('file', { cert_id: it.id, inline: 1 })
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Could not open the file') }); return }
    window.open(r.url, '_blank', 'noopener')
  }
  const activate = async (it) => {
    const d = draftFor(it)
    const t = typeOf(d)
    if (!t) { setMsg({ kind: 'warn', text: `Pick a certification type for ${it.employee_name}'s file first.` }); return }
    if (t.requires_expiry !== false && !d.expires_date) { setMsg({ kind: 'warn', text: `${t.name} expires — read the expiry date off ${it.file_name || 'the card'} and enter it.` }); return }
    setBusyId(it.id); setMsg(null)
    const r = await certsPost('classify', {
      cert_id: it.id, cert_type_id: Number(d.cert_type_id),
      issued_date: d.issued_date || null, expires_date: d.expires_date || null,
      cert_number: d.cert_number, issuing_org: d.issuing_org, notes: d.notes,
    })
    setBusyId(null)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Save failed') }); return }
    setMsg({ kind: 'ok', text: `Activated ${t.name} for ${it.employee_name}.` })
    onChanged()
  }
  const reject = async (it) => {
    const reason = window.prompt(`Reject ${it.file_name || 'this file'} for ${it.employee_name}? It is kept for the record but never counts as a certification. Reason (optional):`)
    if (reason === null) return
    setBusyId(it.id); setMsg(null)
    const r = await certsPost('classify', { cert_id: it.id, reject: true, notes: reason })
    setBusyId(null)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Reject failed') }); return }
    setMsg({ kind: 'ok', text: 'Rejected.' })
    onChanged()
  }

  const small = { ...inp, padding: '4px 6px', fontSize: 12 }
  return (
    <div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e2d3d' }}>{items.length} file{items.length === 1 ? '' : 's'} waiting for review</span>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Open the file, pick the type if it is not already set, type the dates off the card, then Save &amp; activate. Until then the file does not count as a certification anywhere.</span>
      </div>
      <Msg msg={msg} />
      {items.length === 0 && <div style={{ ...card, fontSize: 13, color: '#6b7280' }}>Nothing waiting. 🎉</div>}
      {items.map(it => {
        const d = draftFor(it)
        const t = typeOf(d)
        const busy = busyId === it.id
        return (
          <div key={it.id} style={{ ...card, background: t ? '#fff' : '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, cursor: 'pointer' }} onClick={() => onOpenEmployee(it.employee_id)}>{it.employee_name} <span style={{ color: '#6b7280', fontWeight: 400 }}>· #{it.employee_id}{it.employee_active === false ? ' · inactive' : ''}</span></span>
              <span style={{ fontSize: 12, color: '#4a5568', fontFamily: 'monospace' }}>{it.file_name || it.import_source_path || '(no file)'}</span>
              {it.has_file && <button className="btn btn-sm" onClick={() => view(it)}>👁 View</button>}
              {it.uploaded_by === 'text message' && <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#E6F1FB', color: '#185FA5', fontWeight: 600 }}>Sent by text</span>}
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{it.uploaded_by ? `from ${it.uploaded_by}` : ''}{it.cert_type_id ? (it.uploaded_by === 'text message' ? ' · type from the expiry text' : ' · type matched by the import') : ' · type not recognised'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 2fr) repeat(4, minmax(120px, 1fr))', gap: 8, alignItems: 'end' }}>
              <label style={{ fontSize: 11, color: '#4a5568' }}>Certification type
                <select style={{ ...small, width: '100%', marginTop: 2, borderColor: t ? '#cfd6dd' : '#f59e0b' }} value={d.cert_type_id} onChange={e => onType(it, e.target.value)}>
                  <option value="">— pick from the standard list —</option>
                  {activeTypes.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 11, color: '#4a5568' }}>Issued
                <input type="date" style={{ ...small, width: '100%', marginTop: 2 }} value={d.issued_date} onChange={e => onIssued(it, e.target.value)} />
              </label>
              <label style={{ fontSize: 11, color: '#4a5568' }}>Expires{t && t.requires_expiry === false ? ' (optional)' : ''}
                <input type="date" style={{ ...small, width: '100%', marginTop: 2, borderColor: t && t.requires_expiry !== false && !d.expires_date ? '#f59e0b' : '#cfd6dd' }} value={d.expires_date} onChange={e => setDraft(it, { expires_date: e.target.value, expires_touched: true })} />
              </label>
              <label style={{ fontSize: 11, color: '#4a5568' }}>Cert #
                <input style={{ ...small, width: '100%', marginTop: 2 }} value={d.cert_number} onChange={e => setDraft(it, { cert_number: e.target.value })} />
              </label>
              <label style={{ fontSize: 11, color: '#4a5568' }}>Issuing org
                <input style={{ ...small, width: '100%', marginTop: 2 }} value={d.issuing_org} onChange={e => setDraft(it, { issuing_org: e.target.value })} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <input style={{ ...small, flex: 1, minWidth: 200 }} placeholder="Notes" value={d.notes} onChange={e => setDraft(it, { notes: e.target.value })} />
              <button className="btn btn-sm" disabled={busy} onClick={() => reject(it)}>Reject</button>
              <button className="btn btn-sm btn-primary" disabled={busy || !t} onClick={() => activate(it)}>{busy ? 'Saving…' : 'Save & activate'}</button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ExpiringWorklist({ onOpenEmployee, refreshKey }) {
  const [days, setDays] = useState(60)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    let alive = true
    certsGet('expiring', { days }).then(r => {
      if (!alive) return
      if (r.error) { setErr(errText(r, 'Could not load the worklist')); return }
      setData(r)
    })
    return () => { alive = false }
  }, [days, refreshKey])
  const items = data ? data.items : []
  const expired = items.filter(i => i.state === 'expired')
  const expiring = items.filter(i => i.state !== 'expired')
  const Row = ({ i }) => (
    <tr>
      <td style={{ ...td, color: '#6b7280' }}>{i.employee_id}</td>
      <td style={{ ...td, fontWeight: 600 }}>{i.employee_name}</td>
      <td style={td}>{i.cert_type_name}</td>
      <td style={td}>{fmtDate(i.expires_date)}</td>
      <td style={td}><Pill state={i.state} text={i.days_left <= 0 ? (i.days_left === 0 ? 'today' : `${-i.days_left}d ago`) : `${i.days_left}d`} /></td>
      <td style={td}><button className="btn btn-sm" onClick={() => onOpenEmployee(i.employee_id)}>Open</button></td>
    </tr>
  )
  const Table = ({ list }) => (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead><tr><th style={th}>Emp #</th><th style={th}>Name</th><th style={th}>Certification</th><th style={th}>Expires</th><th style={th}>Days</th><th style={th}></th></tr></thead>
      <tbody>{list.map(i => <Row key={i.id} i={i} />)}</tbody>
    </table>
  )
  return (
    <div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e2d3d' }}>Expiring within</span>
        <select style={inp} value={days} onChange={e => setDays(Number(e.target.value))}>
          {[14, 30, 60, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
        </select>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Active employees only. This is the same list the 8 AM digest email is built from.</span>
      </div>
      {err && <Msg msg={{ kind: 'error', text: err }} />}
      {data && (
        <>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>Expired ({expired.length})</div>
            {expired.length ? <Table list={expired} /> : <div style={{ fontSize: 12, color: '#6b7280' }}>Nothing expired. 🎉</div>}
          </div>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>Expiring within {days} days ({expiring.length})</div>
            {expiring.length ? <Table list={expiring} /> : <div style={{ fontSize: 12, color: '#6b7280' }}>Nothing expiring in that window.</div>}
          </div>
        </>
      )}
    </div>
  )
}

const CATEGORIES = ['osha', 'medical', 'equipment', 'hazard_awareness', 'tool', 'site_orientation', 'other']
const emptyType = () => ({ id: null, name: '', short_name: '', category: 'other', default_valid_months: '', requires_expiry: true, site_specific: false, aliases: '', notes: '', sort_order: 100 })

function CertTypesEditor({ opts, onChanged }) {
  const [edit, setEdit] = useState(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const types = opts.cert_types

  const save = async () => {
    if (!edit.name.trim()) { setMsg({ kind: 'warn', text: 'A name is required.' }); return }
    setBusy(true); setMsg(null)
    const r = await certsPost('cert-type-save', { ...edit, aliases: edit.aliases })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Save failed') }); return }
    setEdit(null); setMsg({ kind: 'ok', text: `Saved "${r.cert_type.name}".` }); onChanged()
  }
  const archive = async (t, active) => {
    if (!active && !window.confirm(`Archive "${t.name}"? Existing certificates keep it; it just leaves the pickers.`)) return
    setBusy(true)
    const r = await certsPost('cert-type-archive', { id: t.id, active })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Update failed') }); return }
    onChanged()
  }

  return (
    <div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#4a5568' }}>The ONE canonical name per certification. Add a type here first; the Add-cert form only offers this list.</div>
        <button className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setEdit(emptyType())}>+ Add cert type</button>
      </div>
      <Msg msg={msg} />
      {edit && (
        <div style={{ ...card, background: '#f8fafc' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{edit.id ? 'Edit cert type' : 'New cert type'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            <label style={{ fontSize: 12 }}>Name<input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} /></label>
            <label style={{ fontSize: 12 }}>Short name (matrix column)<input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={edit.short_name || ''} onChange={e => setEdit({ ...edit, short_name: e.target.value })} /></label>
            <label style={{ fontSize: 12 }}>Category<select style={{ ...inp, width: '100%' }} value={edit.category} onChange={e => setEdit({ ...edit, category: e.target.value })}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></label>
            <label style={{ fontSize: 12 }}>Default validity (months, blank = none)<input type="number" min="1" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={edit.default_valid_months ?? ''} onChange={e => setEdit({ ...edit, default_valid_months: e.target.value })} /></label>
            <label style={{ fontSize: 12 }}>Sort order<input type="number" style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={edit.sort_order ?? 100} onChange={e => setEdit({ ...edit, sort_order: e.target.value })} /></label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 }}><input type="checkbox" checked={!!edit.requires_expiry} onChange={e => setEdit({ ...edit, requires_expiry: e.target.checked })} /> Requires an expiry date</label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 }}><input type="checkbox" checked={!!edit.site_specific} onChange={e => setEdit({ ...edit, site_specific: e.target.checked })} /> Site-specific</label>
          </div>
          <label style={{ fontSize: 12, display: 'block', marginTop: 8 }}>Aliases (other spellings, comma-separated — for search and the future importer)
            <input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={edit.aliases} onChange={e => setEdit({ ...edit, aliases: e.target.value })} placeholder="Osha 30, OSHA-30, Osha 30 Certification" />
          </label>
          <label style={{ fontSize: 12, display: 'block', marginTop: 8 }}>Notes<input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} value={edit.notes || ''} onChange={e => setEdit({ ...edit, notes: e.target.value })} /></label>
          <div className="modal-actions">
            <button className="btn" onClick={() => setEdit(null)} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr><th style={th}>Name</th><th style={th}>Short</th><th style={th}>Category</th><th style={th}>Default months</th><th style={th}>Expires?</th><th style={th}>Aliases</th><th style={th}></th></tr></thead>
          <tbody>
            {types.map(t => (
              <tr key={t.id} style={{ opacity: t.active ? 1 : 0.55 }}>
                <td style={{ ...td, fontWeight: 600 }}>{t.name}{!t.active && <span style={{ color: '#9ca3af', fontWeight: 400 }}> · archived</span>}{t.site_specific && <span style={{ color: '#6b7280', fontWeight: 400 }}> · site-specific</span>}</td>
                <td style={td}>{t.short_name || ''}</td>
                <td style={td}>{t.category}</td>
                <td style={td}>{t.default_valid_months ?? '—'}</td>
                <td style={td}>{t.requires_expiry ? 'yes' : 'no'}</td>
                <td style={{ ...td, fontSize: 11, color: '#6b7280', maxWidth: 320 }}>{(t.aliases || []).join(', ')}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm" disabled={busy} onClick={() => setEdit({ ...t, default_valid_months: t.default_valid_months ?? '', aliases: (t.aliases || []).join(', '), notes: t.notes || '', short_name: t.short_name || '' })}>Edit</button>{' '}
                  {t.active
                    ? <button className="btn btn-sm" disabled={busy} onClick={() => archive(t, false)}>Archive</button>
                    : <button className="btn btn-sm" disabled={busy} onClick={() => archive(t, true)}>Restore</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// PER-EMPLOYEE CERT PANEL (modal) — read for everyone, manage for Paula
function EmployeeCertsModal({ employeeId, opts, canManage, onClose, onChanged }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [form, setForm] = useState(null) // { mode: 'add'|'replace'|'edit', cert }
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const load = useCallback(async () => {
    const r = await certsGet('list', { employee_id: employeeId })
    if (r.error) { setErr(errText(r, 'Could not load this employee')); return }
    setData(r)
  }, [employeeId])
  useEffect(() => { load() }, [load])

  // A pending_review row may have NO type yet (Phase 3 import) — say so rather than "type #null".
  const typeName = (id) => id == null ? 'Unclassified — needs review' : ((opts.cert_types.find(t => String(t.id) === String(id)) || {}).name || `type #${id}`)
  const certs = data ? data.certs : []
  const live = certs.filter(c => c.status === 'active' || c.status === 'pending_review')
  const history = certs.filter(c => c.status === 'superseded' || c.status === 'rejected')

  const remove = async (c) => {
    const reason = window.prompt(`Remove ${typeName(c.cert_type_id)} for ${data.employee.name}? The file is kept for the record. Reason (optional):`)
    if (reason === null) return
    setBusy(true)
    const r = await certsPost('delete', { cert_id: c.id, reason })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Remove failed') }); return }
    setMsg({ kind: 'ok', text: 'Removed.' }); load(); onChanged()
  }
  const restore = async (c) => {
    setBusy(true)
    const r = await certsPost('update', { cert_id: c.id, status: 'active' })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Restore failed') }); return }
    load(); onChanged()
  }
  const dl = async (c) => {
    const r = await downloadCert(c.id)
    if (r.error) setMsg({ kind: 'error', text: errText(r, 'Download failed') })
  }

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ paddingTop: 30 }}>
      <div className="modal" style={{ width: 820, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <div className="modal-title">{data ? data.employee.name : 'Employee'} <span style={{ color: '#6b7280', fontWeight: 400 }}>· #{employeeId}</span></div>
            <div className="modal-sub">{data ? `${data.employee.local || ''}${data.employee.active === false ? ' · inactive' : ''}` : ''}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {canManage && !form && <button className="btn btn-sm btn-primary" onClick={() => setForm({ mode: 'add', cert: null })}>+ Add cert</button>}
            <button className="btn btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        {err && <Msg msg={{ kind: 'error', text: err }} />}
        <Msg msg={msg} />

        {form && data && (
          <CertForm mode={form.mode} cert={form.cert} employee={data.employee} opts={opts}
            onCancel={() => setForm(null)}
            onSaved={(t) => { setForm(null); setMsg({ kind: 'ok', text: t }); load(); onChanged() }} />
        )}

        {data && !form && (
          <>
            <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 6 }}>
              <thead><tr><th style={th}>Certification</th><th style={th}>Issued</th><th style={th}>Expires</th><th style={th}>Status</th><th style={th}>#</th><th style={th}>File</th><th style={th}></th></tr></thead>
              <tbody>
                {live.length === 0 && <tr><td style={td} colSpan={7}><span style={{ color: '#6b7280' }}>No certifications on file.</span></td></tr>}
                {live.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{typeName(c.cert_type_id)}{c.issuing_org && <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>{c.issuing_org}</div>}</td>
                    <td style={td}>{fmtDate(c.issued_date) || '—'}</td>
                    <td style={td}>{c.expires_date ? fmtDate(c.expires_date) : <span style={{ color: '#6b7280' }}>no expiry</span>}</td>
                    <td style={td}><Pill state={c.state} text={c.state === 'expiring' ? `Expiring · ${c.days_left}d` : c.state === 'expired' ? `Expired · ${-c.days_left}d ago` : undefined} /></td>
                    <td style={{ ...td, fontSize: 12, color: '#6b7280' }}>{c.cert_number || ''}</td>
                    <td style={td}>{c.has_file ? <button className="btn btn-sm" onClick={() => dl(c)}>⬇ {c.file_name ? c.file_name.slice(0, 22) : 'file'}</button> : <span style={{ fontSize: 11, color: '#9ca3af' }}>none</span>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {canManage && c.status === 'pending_review' && (
                        <span style={{ fontSize: 11, color: '#1e40af' }}>classify it under Manage → Needs review</span>
                      )}
                      {canManage && c.status !== 'pending_review' && (
                        <>
                          <button className="btn btn-sm" disabled={busy} onClick={() => setForm({ mode: 'replace', cert: c })} title="Renewal: upload the new card; this one becomes superseded">Replace</button>{' '}
                          <button className="btn btn-sm" disabled={busy} onClick={() => setForm({ mode: 'edit', cert: c })}>Edit</button>{' '}
                          <button className="btn btn-sm" disabled={busy} onClick={() => remove(c)}>Remove</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {history.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <button className="btn btn-sm" onClick={() => setShowHistory(h => !h)}>{showHistory ? 'Hide' : 'Show'} history ({history.length})</button>
                {showHistory && (
                  <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 6, opacity: 0.8 }}>
                    <tbody>
                      {history.map(c => (
                        <tr key={c.id}>
                          <td style={td}>{typeName(c.cert_type_id)}</td>
                          <td style={td}>{fmtDate(c.issued_date) || '—'} → {c.expires_date ? fmtDate(c.expires_date) : 'no expiry'}</td>
                          <td style={td}><Pill state={c.state} /></td>
                          <td style={{ ...td, fontSize: 11, color: '#6b7280', whiteSpace: 'pre-wrap' }}>{c.notes || ''}</td>
                          <td style={td}>{c.has_file && <button className="btn btn-sm" onClick={() => dl(c)}>⬇</button>}</td>
                          <td style={td}>{canManage && c.status === 'rejected' && <button className="btn btn-sm" disabled={busy} onClick={() => restore(c)}>Restore</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Add / Replace / Edit form. Cert type is ALWAYS a <select> over cert_types.
function CertForm({ mode, cert, employee, opts, onCancel, onSaved }) {
  const activeTypes = opts.cert_types.filter(t => t.active)
  const [typeId, setTypeId] = useState(cert ? String(cert.cert_type_id) : '')
  const [issued, setIssued] = useState(cert && mode === 'edit' ? (cert.issued_date || '') : '')
  const [expires, setExpires] = useState(cert && mode === 'edit' ? (cert.expires_date || '') : '')
  const [noExpiry, setNoExpiry] = useState(cert && mode === 'edit' ? !cert.expires_date : false)
  const [expiresTouched, setExpiresTouched] = useState(mode === 'edit')
  const [number, setNumber] = useState(cert && mode === 'edit' ? (cert.cert_number || '') : '')
  const [org, setOrg] = useState(cert ? (cert.issuing_org || '') : '')
  const [notes, setNotes] = useState(cert && mode === 'edit' ? (cert.notes || '') : '')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const type = activeTypes.find(t => String(t.id) === typeId) || opts.cert_types.find(t => String(t.id) === typeId)

  // PRE-FILL ONLY: issued + the type's default_valid_months → expires, until the
  // user touches the expiry field. The server stores exactly what is sent.
  useEffect(() => {
    if (expiresTouched || noExpiry) return
    if (type && type.default_valid_months && issued) setExpires(addMonthsYmd(issued, type.default_valid_months))
    else if (type && !type.default_valid_months && !type.requires_expiry) { setExpires(''); setNoExpiry(true) }
  }, [issued, type, expiresTouched, noExpiry])

  const save = async () => {
    if (!typeId) { setMsg({ kind: 'warn', text: 'Pick a certification type.' }); return }
    if (type && type.requires_expiry && !noExpiry && !expires) { setMsg({ kind: 'warn', text: `${type.name} needs an expiry date (or tick "does not expire").` }); return }
    setBusy(true); setMsg(null)
    let fileDesc = null
    if (file) {
      setMsg({ kind: 'ok', text: `Uploading ${file.name} (${mb(file.size)})…` })
      const up = await uploadCertFile(employee.id, file)
      if (up.error) { setBusy(false); setMsg({ kind: 'error', text: errText(up, 'Upload failed') }); return }
      fileDesc = up
    }
    const payload = {
      employee_id: employee.id, cert_type_id: Number(typeId),
      issued_date: issued || null, expires_date: noExpiry ? null : (expires || null),
      cert_number: number, issuing_org: org, notes,
    }
    let r
    if (mode === 'edit') r = await certsPost('update', { cert_id: cert.id, cert_type_id: payload.cert_type_id, issued_date: payload.issued_date, expires_date: payload.expires_date, cert_number: number, issuing_org: org, notes })
    else r = await certsPost(mode === 'replace' ? 'replace' : 'add', { ...payload, file: fileDesc })
    setBusy(false)
    if (r.error) { setMsg({ kind: 'error', text: errText(r, 'Save failed') }); return }
    onSaved(mode === 'edit' ? 'Saved.' : mode === 'replace' ? `Renewed ${type ? type.name : 'certification'} — the previous card is now superseded.` : `Added ${type ? type.name : 'certification'}.`)
  }

  const label = { fontSize: 12, color: '#4a5568', display: 'block' }
  const full = { ...inp, width: '100%', boxSizing: 'border-box', marginTop: 3 }
  return (
    <div style={{ ...card, background: '#f8fafc', marginTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
        {mode === 'add' ? 'Add certification' : mode === 'replace' ? `Renew ${cert ? (opts.cert_types.find(t => String(t.id) === String(cert.cert_type_id)) || {}).name : ''}` : 'Edit certification'} — {employee.name}
      </div>
      {mode === 'replace' && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Upload the new card. The current one is kept as history (superseded) so nothing is lost.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <label style={label}>Certification type
          <select style={full} value={typeId} onChange={e => { setTypeId(e.target.value); setExpiresTouched(false); setNoExpiry(false) }} disabled={mode === 'replace'}>
            <option value="">— pick from the standard list —</option>
            {activeTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label style={label}>Issued
          <input type="date" style={full} value={issued} onChange={e => setIssued(e.target.value)} />
        </label>
        <label style={label}>Expires{type && type.default_valid_months && !expiresTouched && issued && !noExpiry ? <span style={{ color: '#6b7280' }}> (pre-filled: {type.default_valid_months} months — check the card)</span> : ''}
          <input type="date" style={full} value={expires} disabled={noExpiry} onChange={e => { setExpires(e.target.value); setExpiresTouched(true) }} />
          <span style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
            <input type="checkbox" checked={noExpiry} onChange={e => { setNoExpiry(e.target.checked); setExpiresTouched(true); if (e.target.checked) setExpires('') }} /> does not expire
          </span>
        </label>
        <label style={label}>Certificate / card number<input style={full} value={number} onChange={e => setNumber(e.target.value)} /></label>
        <label style={label}>Issuing organization<input style={full} value={org} onChange={e => setOrg(e.target.value)} placeholder="OSHA Training Institute, United Academy…" /></label>
        {mode !== 'edit' && (
          <label style={label}>File (PDF, PNG, JPG, HEIC · max 15 MB)
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,application/pdf,image/png,image/jpeg,image/heic" style={{ ...full, padding: 4 }} onChange={e => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
          </label>
        )}
      </div>
      <label style={{ ...label, marginTop: 8 }}>Notes<input style={full} value={notes} onChange={e => setNotes(e.target.value)} /></label>
      <Msg msg={msg} />
      <div className="modal-actions">
        <button className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : mode === 'replace' ? 'Save renewal' : 'Add certification'}</button>
      </div>
    </div>
  )
}
